import { LoggedSets, SetEditor, ExerciseEditor, SessionRecord } from "./RecordEditor";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import type {
  ActiveSession,
  Exercise,
  Pointer,
  ProfileSettings,
  SetResult,
  TemplateExercise,
} from "../shared/types";
import {
  adjustRest,
  advanceAfterSet,
  clearRest,
  currentExercise,
  orderedExercises,
  nextMissingExerciseSet,
  pointerForExercise,
  reconcileWorkoutPointer,
  totalPrescribedSets,
} from "../shared/engine";
import { workoutDurationSeconds, trainingDurationSeconds } from "../shared/workout-duration";
import {
  formatExerciseResult,
  formatLastPerformance,
  historyMetricValue,
} from "../shared/history";
import { exerciseSetValueError } from "../shared/set-values";
import { api, ApiError } from "./api";
import { playRestAlert, primeRestAlert } from "./audio";
import {
  ensureSetDraft,
  initialSetDraft,
  mergeSubmitDraft,
  setDraftKey,
  setDraftFromFormData,
  setValuesFromDraft,
  switchSetDraftSelection,
  updateSetDraft,
  type SetDraftField,
  type SetDrafts,
} from "./set-drafts";
import {
  enqueue,
  flushQueue,
  getActiveSession,
  getSessionRecord,
  getDeviceId,
  persistUndoTransition,
  persistWorkoutTransition,
  removeQueued,
  saveActiveSession,
  saveSessionRecord,
  sessionHasPending,
  type QueuedAction,
} from "./offline";
import { ErrorNotice, Field, formatDate, Modal, Spinner } from "./components";
import { loadWorkoutRecord } from "./session-access";

interface WakeLockSentinelLike {
  released: boolean;
  release(): Promise<void>;
  addEventListener(type: "release", listener: () => void): void;
}
interface ExerciseHistoryRow {
  sessionId: string;
  completedAt: string;
  weightValue: number | null;
  reps: number | null;
  countValue: number | null;
  setNumber: number;
}
interface ExerciseHistoryResponse {
  loadMode: Exercise["loadMode"];
  personalBest: { metric: "weight" | "count" | "reps"; value: number | null };
  trend: ExerciseHistoryRow[];
}

let localMutationSequence = 0;

function reconcileActiveSession(session: ActiveSession): ActiveSession {
  return {
    ...session,
    pointer: reconcileWorkoutPointer(
      session.template,
      session.sets.map((set) => ({
        templateExerciseId: set.templateExerciseId,
        setNumber: set.setNumber,
      })),
      session.pointer,
    ),
  };
}

function loadLabel(item: TemplateExercise) {
  switch (item.exercise.loadMode) {
    case "stack_kg":
      return "Stack kg";
    case "kg_each":
      return "kg each";
    case "kg_total":
      return "kg total";
    case "bodyweight":
      return "Bodyweight";
    case "count":
      return "Count";
  }
}

type WorkoutProps = {
  profileId: string;
  settings: ProfileSettings;
  initial?: ActiveSession;
  onExit: () => void;
  onCompleted: (summary: {
    elapsedSeconds: number;
    completedSets: number;
    progressionFlags: string[];
  }) => void;
  onQueueChange: () => void;
};

function WorkoutView({
  profileId,
  settings,
  initial,
  onExit,
  onCompleted,
  onQueueChange,
}: WorkoutProps) {
  const [session, setSession] = useState<ActiveSession | null>(null);
  const [editSet, setEditSet] = useState<SetResult|null>(null);
  const [editExercise, setEditExercise] = useState<TemplateExercise|null>(null);
  const [historyRecordId, setHistoryRecordId] = useState<string|null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const setDrafts = useRef<SetDrafts>({});
  const touchedDraftFields = useRef<
    Record<string, Partial<Record<SetDraftField, boolean>>>
  >({});
  const [details, setDetails] = useState<TemplateExercise | null>(null);
  const [history, setHistory] = useState<{
    item: TemplateExercise;
    data: ExerciseHistoryResponse | null;
  } | null>(null);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(initial?.notes ?? "");
  const [exerciseNoteFor, setExerciseNoteFor] =
    useState<TemplateExercise | null>(null);
  const [exerciseNoteDraft, setExerciseNoteDraft] = useState("");
  const [substituteFor, setSubstituteFor] = useState<TemplateExercise | null>(
    null,
  );
  const [library, setLibrary] = useState<Exercise[]>([]);
  const [substituteId, setSubstituteId] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [draftGeneration, setDraftGeneration] = useState(0);
  const chimePlayedFor = useRef<string | null>(null);
  const completionShown = useRef(false);
  const wakeLock = useRef<WakeLockSentinelLike | null>(null);
  const submittingSet = useRef(false);
  const setForm = useRef<HTMLFormElement | null>(null);
  const boundDraftInputs = useRef(new WeakSet<HTMLInputElement>());
  const initializedDraftInputs = useRef(
    new WeakMap<HTMLInputElement, string>(),
  );
  const item = useMemo(
    () => (session ? currentExercise(session.template, session.pointer) : null),
    [session],
  );
  const exercises = useMemo(
    () => (session ? orderedExercises(session.template) : []),
    [session?.template],
  );
  const exerciseIndex = item
    ? exercises.findIndex((entry) => entry.id === item.id)
    : -1;
  const completed = session?.sets.length ?? 0;
  const total = session ? totalPrescribedSets(session.template) : 0;
  const readyToFinish = Boolean(session && total > 0 && completed >= total);
  const completedSlots = useMemo(
    () =>
      session?.sets.map((set) => ({
        templateExerciseId: set.templateExerciseId,
        setNumber: set.setNumber,
      })) ?? [],
    [session?.sets],
  );
  const unfinishedOtherCount = item
    ? exercises.filter(
        (exercise) =>
          exercise.id !== item.id &&
          nextMissingExerciseSet(exercise, completedSlots) !== null,
      ).length
    : 0;
  const currentExerciseHasSets = Boolean(
    session &&
    item &&
    session.sets.some((set) => set.templateExerciseId === item.id),
  );
  const activeDraftKey =
    session && item
      ? setDraftKey(
          session.id,
          item.id,
          session.pointer.round,
          item.exercise.id,
        )
      : null;
  const activeDraftKeyRef = useRef<string | null>(activeDraftKey);
  activeDraftKeyRef.current = activeDraftKey;
  const defaultDraft =
    session && item
      ? initialSetDraft(session, item)
      : { weight: "", reps: "", count: "" };
  const activeDraft =
    (activeDraftKey ? setDrafts.current[activeDraftKey] : undefined) ??
    defaultDraft;
  const { weight, reps, count } = activeDraft;

  function recordDraftField(
    slotKey: string,
    field: SetDraftField,
    value: string,
  ) {
    // WebKit can finish dispatching an input event after its form has been
    // detached by an exercise switch. Never let that stale node resurrect a
    // draft which navigation explicitly discarded.
    if (slotKey !== activeDraftKeyRef.current) return;
    const defaults =
      slotKey === activeDraftKey
        ? defaultDraft
        : {
            weight: "",
            reps: "",
            count: "",
          };
    setDrafts.current = updateSetDraft(
      setDrafts.current,
      slotKey,
      defaults,
      field,
      value,
    );
    touchedDraftFields.current[slotKey] = {
      ...touchedDraftFields.current[slotKey],
      [field]: true,
    };
  }

  function changeSetDraft(field: SetDraftField, value: string) {
    if (!activeDraftKey) return;
    recordDraftField(activeDraftKey, field, value);
  }
  const setWeight = (value: string) => changeSetDraft("weight", value);

  function initializeDraftInput(
    input: HTMLInputElement,
    field: SetDraftField,
    initialValue: string,
  ) {
    const slot = `${activeDraftKey ?? "none"}:${field}`;
    if (initializedDraftInputs.current.get(input) === slot) return;
    // Keep the browser as the owner of an in-progress field. React updating a
    // number input's defaultValue during an unrelated render can restore the
    // empty default in WebKit between a real input event and form submission.
    // The keyed form creates a fresh element for a genuinely new set; within
    // one set, this one-time initializer never touches the user's DOM value.
    input.value = initialValue;
    initializedDraftInputs.current.set(input, slot);
    if (!boundDraftInputs.current.has(input) && activeDraftKey) {
      const slotKey = activeDraftKey;
      // Bind to the exact native input rather than React's delegated handler
      // or the form. Even if a stale render replaces/detaches this element
      // during Safari input, its native event records the newest value first.
      input.addEventListener(
        "input",
        () => recordDraftField(slotKey, field, input.value),
        true,
      );
      boundDraftInputs.current.add(input);
    }
  }

  useEffect(() => {
    const captureInput = (event: Event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.form) return;
      if (!(["weight", "reps", "count"] as string[]).includes(input.name))
        return;
      const slotKey = input.form.dataset.slotKey;
      if (!slotKey) return;
      const field = input.name as SetDraftField;
      recordDraftField(slotKey, field, input.value);
    };
    // This lifecycle-independent capture sees any still-connected replacement
    // input before React's delegated handlers. The per-input listener above
    // covers the complementary case where Safari targets a detached element.
    window.addEventListener("input", captureInput, true);
    return () => window.removeEventListener("input", captureInput, true);
  }, [profileId]);

  useEffect(() => {
    if (
      !session ||
      !readyToFinish ||
      !session.finalSetAt ||
      completionShown.current
    )
      return;
    completionShown.current = true;
    onCompleted({
      elapsedSeconds:
        session.elapsedSeconds ??
        workoutDurationSeconds({
          firstSetAt: session.firstSetAt,
          finalSetAt: session.finalSetAt,
        }),
      completedSets: session.sets.length,
      progressionFlags: [],
    });
    void syncQueued();
  }, [readyToFinish, session?.finalSetAt]);

  useEffect(() => {
    let current = true;
    (async () => {
      const local = await getActiveSession(profileId);
      let candidate=initial??local;
      if(!initial&&navigator.onLine) {
        await flushQueue(profileId);
        const remote=await api<ActiveSession|undefined>('/sessions/active',{headers:{'X-Hardnup-Profile':profileId}});
        candidate=local&&await sessionHasPending(profileId,local.id)?local:remote??null;
      }
      return candidate ? loadWorkoutRecord(profileId,candidate.id) : null;
    })()
      .then(async (value) => {
        if (!current) return;
        if (value) {
          const repaired = reconcileActiveSession(value);
          await saveActiveSession(profileId,repaired);
          setSession(repaired);
          setNotesDraft(repaired.notes ?? "");
        } else setError("No active workout was found.");
      })
      .catch(async (caught) => {
        if(caught instanceof ApiError) {if(current) setError(caught.message);return;}
        const local = initial ? await getSessionRecord(profileId,initial.id) : await getActiveSession(profileId);
        if (!current) return;
        if (local) {
          const repaired = reconcileActiveSession(local);
          setSession(repaired);
          setNotesDraft(repaired.notes ?? "");
          setStatus("Offline copy restored.");
        } else setError("No saved active workout was found.");
      });
    return () => {current=false;};
  }, []);
  useEffect(() => {
    if (!session) return;
    const updateElapsed = () => {
      if (!session.firstSetAt) {
        setElapsed(0);
        return;
      }
      setElapsed(
        trainingDurationSeconds(session.sets.map(set=>set.completedAt),settings.timezone,session.finalSetAt ?? new Date().toISOString()),
      );
    };
    updateElapsed();
    if (session.finalSetAt) return;
    const timer = setInterval(updateElapsed, 1000);
    return () => clearInterval(timer);
  }, [session?.sets, session?.firstSetAt, session?.finalSetAt, settings.timezone]);
  useEffect(() => {
    if (!session?.pointer.selectionRequired || readyToFinish) return;
    document.getElementById("choose-next-exercise")?.focus();
  }, [session?.pointer.selectionRequired, readyToFinish]);

  useEffect(() => {
    if (!session?.pointer.restEndAt) {
      setSecondsLeft(0);
      return;
    }
    const end = session.pointer.restEndAt;
    const tick = () => {
      const remaining = Math.max(
        0,
        Math.ceil((new Date(end).getTime() - Date.now()) / 1000),
      );
      setSecondsLeft(remaining);
      if (remaining === 0 && chimePlayedFor.current !== end) {
        chimePlayedFor.current = end;
        const alertKey = `hardnup-rest-alerted:${profileId}`;
        const alreadyAlerted = localStorage.getItem(alertKey) === end;
        localStorage.setItem(alertKey, end);
        if (!alreadyAlerted) void playRestAlert(settings.alertMode);
        const pointer = clearRest(session.pointer);
        const updated = { ...session, pointer };
        setSession(updated);
        void saveSessionRecord(profileId, updated);
        void persistPointer(updated, pointer);
        setStatus("Rest finished. Next set ready.");
      }
    };
    tick();
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [session?.pointer.restEndAt, settings.alertMode]);

  useEffect(() => {
    if (!settings.keepAwake) {
      void wakeLock.current?.release();
      wakeLock.current = null;
      return;
    }
    let mounted = true;
    async function request() {
      if (!session) return;
      try {
        const nav = navigator as Navigator & {
          wakeLock?: { request(type: "screen"): Promise<WakeLockSentinelLike> };
        };
        if (nav.wakeLock && !wakeLock.current) {
          wakeLock.current = await nav.wakeLock.request("screen");
          wakeLock.current.addEventListener("release", () => {
            wakeLock.current = null;
          });
        }
      } catch {
        setStatus("Screen wake lock is unavailable; keep the app visible.");
      }
    }
    void request();
    const visible = () => {
      if (document.visibilityState === "visible" && mounted) void request();
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      mounted = false;
      document.removeEventListener("visibilitychange", visible);
      void wakeLock.current?.release();
      wakeLock.current = null;
    };
  }, [Boolean(session), settings.keepAwake]);

  function queueAction(
    id: string,
    path: string,
    method: QueuedAction["method"],
    body?: unknown,
  ): QueuedAction {
    return {
      id,
      profileId,
      path,
      method,
      body,
      createdAt: new Date().toISOString(),
      sequence: ++localMutationSequence,
    };
  }

  async function syncQueued(successStatus?: string) {
    const result = await flushQueue(profileId);
    onQueueChange();
    if (result.remaining)
      setStatus("Saved on this phone. Waiting to synchronise with your server.");
    else if (successStatus) setStatus(successStatus);
    return result;
  }

  async function persistPointer(updated: ActiveSession, pointer: Pointer) {
    const action = queueAction(
      `pointer-${updated.id}`,
      `/sessions/${updated.id}/pointer`,
      "PATCH",
      pointer,
    );
    await persistWorkoutTransition(profileId, updated, [action]);
    onQueueChange();
    void syncQueued();
  }

  function selectExercise(selected: TemplateExercise) {
    if (!session || busy) return;
    const nextSet = nextMissingExerciseSet(selected, completedSlots) ?? selected.sets;
    const pointer = pointerForExercise(session.template, selected.id, nextSet);
    if (!pointer) return;
    const key = setDraftKey(
      session.id,
      selected.id,
      nextSet,
      selected.exercise.id,
    );
    const updated = { ...session, pointer };
    const previousKey = activeDraftKeyRef.current;
    // Invalidate the old native form before React detaches it. A target-level
    // input listener may otherwise finish later in the same Safari event turn.
    activeDraftKeyRef.current = key;
    // Safari can dispatch the next real/user-automation input event before a
    // batched selection render commits. Commit the newly keyed form before the
    // selection handler returns so values can never land in the previous form.
    flushSync(() => {
      setDrafts.current = switchSetDraftSelection(
        setDrafts.current,
        previousKey,
        key,
        initialSetDraft(session, selected),
      );
      if (previousKey && previousKey !== key)
        delete touchedDraftFields.current[previousKey];
      if (previousKey !== key) delete touchedDraftFields.current[key];
      setError("");
      setSession(updated);
    });
    if(session.status !== "partial") void persistPointer(updated, pointer);
    // The flush above has committed the selected card, so move announced focus
    // now. A deferred animation-frame focus can arrive after the user has
    // already started typing on iOS/WebKit and cancel that edit.
    document.getElementById("exercise-name")?.focus();
  }

  async function doneSet(form: HTMLFormElement) {
    if (
      !session ||
      !item ||
      session.pointer.restEndAt ||
      readyToFinish ||
      session.status === "partial" ||
      item.finishedEarly ||
      nextMissingExerciseSet(item, completedSlots) === null ||
      submittingSet.current
    )
      return;
    if (!activeDraftKey || form.dataset.slotKey !== activeDraftKey) {
      setError("The selected set changed. Check the values and try again.");
      return;
    }
    submittingSet.current = true;
    const formDraft = setDraftFromFormData(new FormData(form));
    const eventDraft = setDrafts.current[activeDraftKey] ?? defaultDraft;
    const submittedDraft = mergeSubmitDraft(
      formDraft,
      eventDraft,
      touchedDraftFields.current[activeDraftKey] ?? {},
    );
    setDrafts.current = {
      ...setDrafts.current,
      [activeDraftKey]: submittedDraft,
    };
    setError("");
    setBusy(true);
    void primeRestAlert(settings.alertMode);
    const {
      weightValue,
      reps: repsValue,
      countValue,
    } = setValuesFromDraft(item.exercise.loadMode, submittedDraft);
    const valueError = exerciseSetValueError(item.exercise.loadMode, {
      weightValue,
      reps: repsValue,
      countValue,
    });
    if (valueError) {
      setError(valueError);
      setBusy(false);
      submittingSet.current = false;
      return;
    }
    const previousPointer = { ...session.pointer };
    const result: SetResult = {
      clientEventId: crypto.randomUUID(),
      templateExerciseId: item.id,
      exerciseId: item.exercise.id,
      setNumber: session.pointer.round,
      weightValue,
      reps: repsValue,
      countValue,
      completedAt: new Date().toISOString(),
      previousPointer,
      pending: true,
    };
    const advance = advanceAfterSet(
      session.template,
      session.pointer,
      Date.now(),
      [...session.sets, result],
    );
    const firstSetAt = session.firstSetAt ?? result.completedAt;
    const finalSetAt = advance.finished ? result.completedAt : null;
    const updated: ActiveSession = {
      ...session,
      status: advance.finished ? 'completed' : 'active',
      sets: [...session.sets, result],
      pointer: advance.pointer,
      firstSetAt,
      finalSetAt,
      elapsedSeconds: finalSetAt
        ? trainingDurationSeconds([...session.sets,result].map(set=>set.completedAt),settings.timezone)
        : null,
    };
    const body = { ...result, sourceDeviceId: getDeviceId() };
    const setAction = queueAction(
      result.clientEventId,
      `/sessions/${session.id}/sets`,
      "POST",
      body,
    );
    const actions = [setAction];
    if (!advance.finished)
      actions.push(
        queueAction(
          `pointer-${session.id}`,
          `/sessions/${session.id}/pointer`,
          "PATCH",
          advance.pointer,
        ),
      );
    else
      actions.push(
        queueAction(
          `complete-${session.id}`,
          `/sessions/${session.id}/complete`,
          "POST",
          { clientEventId: session.id },
        ),
      );
    try {
      await persistWorkoutTransition(profileId, updated, actions);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "This set could not be saved on this device.",
      );
      setBusy(false);
      submittingSet.current = false;
      return;
    }
    setSession(updated);
    setStatus("Saved on phone. Syncing safely…");
    setBusy(false);
    submittingSet.current = false;
    onQueueChange();
    if (advance.finished) {
      completionShown.current = true;
      onCompleted({
        elapsedSeconds: updated.elapsedSeconds ?? 0,
        completedSets: updated.sets.length,
        progressionFlags: [],
      });
    }
    void (async () => {
      const flushed = await syncQueued(
        advance.finished ? "Workout synced." : "Set synced.",
      );
      if (advance.finished) {
        const completedResponse = flushed.responses[
          `complete-${session.id}`
        ] as
          | {
              elapsedSeconds?: number;
              completedSets?: number;
              progressionFlags?: string[];
            }
          | undefined;
        if (completedResponse)
          onCompleted({
            elapsedSeconds:
              completedResponse.elapsedSeconds ?? updated.elapsedSeconds ?? 0,
            completedSets:
              completedResponse.completedSets ?? updated.sets.length,
            progressionFlags: completedResponse.progressionFlags ?? [],
          });
      }
      const saved = flushed.responses[setAction.id] as
        { id?: string } | undefined;
      if (saved?.id) {
        setSession((current) => {
          if (current?.id !== updated.id) return current;
          const synced = {
            ...current,
            sets: current.sets.map((set) =>
              set.clientEventId === result.clientEventId
                ? { ...set, id: saved.id, pending: false }
                : set,
            ),
          };
          if (!advance.finished) void saveSessionRecord(profileId, synced);
          return synced;
        });
      }
    })();
  }

  function changeRest(delta: number) {
    if (!session) return;
    const pointer = adjustRest(session.pointer, delta);
    const updated = { ...session, pointer };
    setSession(updated);
    void persistPointer(updated, pointer);
  }
  function skipRest() {
    if (!session) return;
    const pointer = clearRest(session.pointer);
    const updated = { ...session, pointer };
    setSession(updated);
    void persistPointer(updated, pointer);
    setStatus("Rest skipped.");
  }

  async function undo() {
    if (!session || !session.sets.length) return;
    const last = session.sets.at(-1)!;
    const pointer = last.previousPointer;
    if (!pointer) {
      setError("This older set cannot be safely undone after reopening.");
      return;
    }
    const remainingSets = session.sets.slice(0, -1);
    const firstSetAt = remainingSets.length
      ? remainingSets.reduce(
          (earliest, set) =>
            new Date(set.completedAt).getTime() < new Date(earliest).getTime()
              ? set.completedAt
              : earliest,
          remainingSets[0].completedAt,
        )
      : null;
    const updated = {
      ...session,
      sets: remainingSets,
      pointer: reconcileWorkoutPointer(
        session.template,
        remainingSets.map((set) => ({
          templateExerciseId: set.templateExerciseId,
          setNumber: set.setNumber,
        })),
        pointer,
      ),
      firstSetAt,
      finalSetAt: null,
      elapsedSeconds: null,
    };
    const tombstone = queueAction(
      `undo-${last.clientEventId}`,
      `/sessions/${session.id}/sets/by-event/${last.clientEventId}`,
      "DELETE",
      { previousPointer: pointer },
    );
    await persistUndoTransition(
      profileId,
      updated,
      [last.clientEventId, `pointer-${session.id}`],
      tombstone,
    );
    setSession(updated);
    setStatus("Last set undone on this phone. Syncing the undo…");
    onQueueChange();
    void syncQueued("Last set undo synced.");
  }

  async function stopForToday() {
    if (!session || busy) return;
    setBusy(true); setError("");
    const eventId = crypto.randomUUID();
    const updated = { ...session, status: 'partial', pointer: {...session.pointer, restEndAt:null} };
    try {
      await persistWorkoutTransition(profileId,updated,[queueAction(eventId,`/records/${session.id}/stop`,"POST",{clientEventId:eventId})]);
      onQueueChange();
      const result = await flushQueue(profileId);
      onQueueChange();
      onExit();
    } catch(e) {setError(e instanceof Error ? e.message : 'Could not stop workout.');}
    finally {setBusy(false);}
  }

  async function finishExerciseEarly(finishedEarly: boolean) {
    if(!session || !item || busy || session.status === "partial") return;
    const eventId=crypto.randomUUID();
    const template={...session.template,exercises:session.template.exercises.map(e=>e.id===item.id?{...e,finishedEarly}:e)};
    const pointer={...session.pointer,restEndAt:null,selectionRequired:finishedEarly};
    const updated={...session,template,pointer:reconcileWorkoutPointer(template,session.sets,pointer)};
    setBusy(true);
    try { await persistWorkoutTransition(profileId,updated,[queueAction(eventId,`/records/${session.id}/exercises/${item.id}/finish`,"PATCH",{clientEventId:eventId,finishedEarly})]);
      setSession(updated); onQueueChange(); void syncQueued();
    } catch(e) {setError(e instanceof Error ? e.message : 'Could not update exercise.');} finally {setBusy(false);}
  }

  function applyRecord(record: ActiveSession) {
    setSession(reconcileActiveSession(record));setEditSet(null);setEditExercise(null);setDetails(null);setHistoryRecordId(null);
    setDrafts.current={};touchedDraftFields.current={};setDraftGeneration(value=>value+1);
    void saveActiveSession(profileId,record);
  }

  async function skipExercise() {
    if (currentExerciseHasSets) {
      setError(
        "This exercise already has a logged set. Complete its remaining sets or undo the logged set before skipping it.",
      );
      return;
    }
    if (
      !session ||
      !item ||
      !window.confirm(`Skip ${item.exercise.name} for this workout only?`)
    )
      return;
    const template = {
      ...session.template,
      exercises: session.template.exercises.filter(
        (entry) => entry.id !== item.id,
      ),
    };
    if (!template.exercises.length) {
      setError("The final exercise cannot be skipped.");
      return;
    }
    const remaining = orderedExercises(template);
    const currentIndex = exercises.findIndex((entry) => entry.id === item.id);
    const next = remaining[Math.min(currentIndex, remaining.length - 1)];
    const pointer = pointerForExercise(template, next.id, 1);
    const body = {
      clientEventId: crypto.randomUUID(),
      templateExerciseId: item.id,
      pointer,
    };
    const action = queueAction(
      `skip-exercise-${session.id}-${item.id}`,
      `/sessions/${session.id}/skip-exercise`,
      "POST",
      body,
    );
    setBusy(true);
    setError("");
    try {
      await enqueue(action);
      onQueueChange();
      const result = await flushQueue(profileId);
      onQueueChange();
      if (result.blocked) {
        if (result.blocked.lastError?.startsWith("409 ")) {
          await removeQueued(profileId, action.id);
          onQueueChange();
          setError(
            "This exercise cannot be skipped because one of its sets is already logged.",
          );
        } else {
          setStatus(
            "Exercise skip is safely queued. Reopen after reconnecting to load the confirmed workout state.",
          );
        }
        return;
      }
      if (result.remaining) {
        setStatus(
          "Exercise skip is safely queued. Reopen after reconnecting to load the confirmed workout state.",
        );
        return;
      }
      const response = result.responses[action.id] as
        { template: ActiveSession["template"]; pointer: Pointer } | undefined;
      if (!response)
        throw new Error("The confirmed exercise skip could not be loaded.");
      const updated = {
        ...session,
        template: response.template,
        pointer: response.pointer,
      };
      await saveActiveSession(profileId, updated);
      setSession(updated);
      setStatus("Exercise skipped for this workout.");
    } catch (caught) {
      // Keep the exact local snapshot/pointer on any server rejection or
      // ambiguous network outcome; reopening reconciles from the server.
      setError(
        caught instanceof Error ? caught.message : "Could not skip exercise",
      );
    } finally {
      setBusy(false);
    }
  }

  async function openSubstitute() {
    if (!item) return;
    setSubstituteFor(item);
    setSubstituteId("");
    if (!library.length)
      try {
        setLibrary(await api<Exercise[]>("/exercises"));
      } catch (caught) {
        setError(
          caught instanceof Error
            ? caught.message
            : "Could not load substitutes",
        );
        setSubstituteFor(null);
      }
  }
  async function substitute() {
    if (!session || !substituteFor || !substituteId) return;
    const exercise = library.find((entry) => entry.id === substituteId);
    if (!exercise) return;
    const template = {
      ...session.template,
      exercises: session.template.exercises.map((entry) =>
        entry.id === substituteFor.id
          ? {
              ...entry,
              exercise,
              // The original exercise's history and progression never belong
              // to the replacement. The server hydrates canonical replacement
              // metadata; until confirmed, an explicit empty state is safer.
              lastPerformance: null,
              progressionFlag: null,
            }
          : entry,
      ),
    };
    const updated = { ...session, template };
    const body = {
      clientEventId: crypto.randomUUID(),
      templateExerciseId: substituteFor.id,
      exerciseId: exercise.id,
    };
    const action = queueAction(
      `substitute-${session.id}-${substituteFor.id}`,
      `/sessions/${session.id}/substitute`,
      "POST",
      body,
    );
    const originalDraftKey = activeDraftKey;
    const replacementItem = template.exercises.find(
      (entry) => entry.id === substituteFor.id,
    )!;
    const replacementDraftKey = setDraftKey(
      session.id,
      replacementItem.id,
      session.pointer.round,
      replacementItem.exercise.id,
    );
    setBusy(true);
    setError("");
    try {
      if (originalDraftKey) {
        delete setDrafts.current[originalDraftKey];
        delete touchedDraftFields.current[originalDraftKey];
      }
      delete setDrafts.current[replacementDraftKey];
      delete touchedDraftFields.current[replacementDraftKey];
      await persistWorkoutTransition(profileId, updated, [action]);
      setSession(updated);
      setSubstituteFor(null);
      onQueueChange();

      const result = await syncQueued();
      const response = result.responses[action.id] as
        { template: ActiveSession["template"] } | undefined;
      if (!response) {
        setStatus(
          "Replacement saved on phone. Its Last time will load after sync.",
        );
        return;
      }
      const confirmed = { ...updated, template: response.template };
      const confirmedItem = confirmed.template.exercises.find(
        (entry) => entry.id === substituteFor.id,
      );
      if (!confirmedItem)
        throw new Error("The confirmed replacement could not be loaded.");
      const confirmedKey = setDraftKey(
        confirmed.id,
        confirmedItem.id,
        confirmed.pointer.round,
        confirmedItem.exercise.id,
      );
      setDrafts.current[confirmedKey] = initialSetDraft(
        confirmed,
        confirmedItem,
      );
      touchedDraftFields.current[confirmedKey] = {};
      await saveActiveSession(profileId, confirmed);
      setSession(confirmed);
      // The actual exercise identity already remounts the form optimistically;
      // this second generation applies canonical Last time/defaults returned
      // by the server. Set-entry controls remain disabled while this hydration
      // is pending, so a newer replacement edit cannot be overwritten.
      setDraftGeneration((value) => value + 1);
      setStatus("Substitution synced.");
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not substitute exercise",
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveNotes() {
    if (!session) return;
    const updated = { ...session, notes: notesDraft };
    const action = queueAction(
      `notes-${session.id}`,
      `/sessions/${session.id}/notes`,
      "PATCH",
      { notes: notesDraft },
    );
    await persistWorkoutTransition(profileId, updated, [action]);
    setSession(updated);
    setNotesOpen(false);
    onQueueChange();
    void syncQueued("Session notes synced.");
  }

  async function saveExerciseNote() {
    if (!session || !exerciseNoteFor) return;
    const template = {
      ...session.template,
      exercises: session.template.exercises.map((entry) =>
        entry.id === exerciseNoteFor.id
          ? { ...entry, notes: exerciseNoteDraft }
          : entry,
      ),
    };
    const updated = { ...session, template };
    const body = {
      clientEventId: crypto.randomUUID(),
      templateExerciseId: exerciseNoteFor.id,
      notes: exerciseNoteDraft,
    };
    const action = queueAction(
      `exercise-note-${session.id}-${exerciseNoteFor.id}`,
      `/sessions/${session.id}/exercise-note`,
      "PATCH",
      body,
    );
    await persistWorkoutTransition(profileId, updated, [action]);
    setSession(updated);
    setExerciseNoteFor(null);
    onQueueChange();
    void syncQueued("Exercise note synced.");
  }

  async function openHistory(historyItem: TemplateExercise) {
    setHistory({ item: historyItem, data: null });
    try {
      setHistory({
        item: historyItem,
        data: await api<ExerciseHistoryResponse>(
          `/exercises/${historyItem.exercise.id}/history`,
        ),
      });
    } catch (caught) {
      setHistory(null);
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not load exercise history",
      );
    }
  }

  function stepLoad(direction: -1 | 1) {
    if (!item) return;
    const input = setForm.current?.elements.namedItem("weight");
    if (!(input instanceof HTMLInputElement)) return;
    const options = item.exercise.loadOptions;
    if (options.length) {
      const current = Number(input.value);
      const ordered = [...options].sort((a, b) => a - b);
      const index =
        direction > 0
          ? ordered.findIndex((value) => value > current)
          : [...ordered].reverse().findIndex((value) => value < current);
      if (index >= 0) {
        const next = String(
          direction > 0 ? ordered[index] : [...ordered].reverse()[index],
        );
        input.value = next;
        setWeight(next);
      }
      return;
    }
    const next = String(
      Math.max(
        item.exercise.defaultIncrement ?? 1,
        (Number(input.value) || 0) +
          direction * (item.exercise.defaultIncrement ?? 1),
      ),
    );
    input.value = next;
    setWeight(next);
  }

  if (!session && !error) return <Spinner label="Restoring workout" />;
  if (!session)
    return (
      <>
        <ErrorNotice message={error} />
        <button className="button button--secondary" onClick={onExit}>
          Back to home
        </button>
      </>
    );
  const previous = item?.lastPerformance;
  const trend = history?.data?.trend.slice(0, 12).reverse() ?? [];
  const trendMax =
    history?.data?.personalBest.value ??
    Math.max(
      1,
      ...trend.map((row) =>
        history?.data
          ? historyMetricValue(history.data.personalBest.metric, row)
          : 0,
      ),
    );

  return (
    <div className="workout-page">
      <header className="workout-header">
        <button
          className="text-button"
          onClick={onExit}
          aria-label="Leave workout screen; your progress remains saved"
        >
          ‹ Home
        </button>
        <div>
          <strong>Workout {session.template.code}</strong>
          <small>
            {Math.floor(elapsed / 60)}:{String(elapsed % 60).padStart(2, "0")} ·{" "}
            {completed}/{total} sets
          </small>
        </div>
        <button
          className="text-button"
          onClick={() => void undo()}
          disabled={!session.sets.length || busy || session.status === "partial"}
        >
          Undo
        </button>
        <progress
          className="progress"
          aria-label="Workout progress"
          max={total}
          value={completed}
        >
          {completed} of {total} sets
        </progress>
      </header>
      {status && (
        <div className="workout-status" role="status">
          {status}
        </div>
      )}
      {error && <ErrorNotice message={error} onDismiss={() => setError("")} />}
      <section
        className="workout-exercise-overview"
        aria-labelledby="workout-exercises-heading"
      >
        <div className="workout-exercise-overview__heading">
          <div>
            <p className="eyebrow">Choose your exercise</p>
            <h1 id="workout-exercises-heading">All exercises</h1>
          </div>

        </div>
        <ol className="workout-exercise-list">
          {exercises.map((exercise, index) => {
            const completedForExercise = new Set(
              completedSlots
                .filter((set) => set.templateExerciseId === exercise.id)
                .map((set) => set.setNumber),
            ).size;
            const isComplete = completedForExercise >= exercise.sets;
            const isCurrent = item?.id === exercise.id;
            const state = isComplete ? "Done" : exercise.finishedEarly ? "Finished early" : completedForExercise ? "In progress" : "Not started";
            return (
              <li
                key={exercise.id}
                className={
                  isComplete ? "is-complete" : `${completedForExercise||exercise.finishedEarly?'is-partial':''} ${isCurrent?'is-current':''}`
                }
              >
                <button
                  type="button"
                  onClick={() => selectExercise(exercise)}
                  aria-current={isCurrent ? "step" : undefined}
                  aria-label={`${index + 1}. ${exercise.exercise.name}. ${state}`}
                  disabled={busy}

                >
                  <span
                    className="workout-exercise-list__number"
                    aria-hidden="true"
                  >
                    {isComplete ? "✓" : index + 1}
                  </span>
                  <span className="workout-exercise-list__name">
                    <strong>{exercise.exercise.name}</strong>
                    <small>{state} · {completedForExercise}/{exercise.sets} sets</small>
                    <small>{session.sets.filter(set=>set.templateExerciseId===exercise.id).sort((a,b)=>a.setNumber-b.setNumber).map(set=>`Set ${set.setNumber}: ${formatExerciseResult(set.loadMode ?? exercise.exercise.loadMode,set)}`).join(" · ")}</small>
                  </span>
                  <span aria-hidden="true">›</span>
                </button>
              </li>
            );
          })}
        </ol>
      </section>
      {!item && !readyToFinish && (
        <section
          className="choose-next-exercise"
          role="status"
          aria-live="polite"
        >
          <h2 id="choose-next-exercise" tabIndex={-1}>
            {exercises.every(exercise=>nextMissingExerciseSet(exercise,completedSlots)===null) ? 'Exercises finished for now' : 'Choose your next exercise'}
          </h2>
          <p>
            {exercises.every(exercise=>nextMissingExerciseSet(exercise,completedSlots)===null)
              ? 'Stop for today to save this partial workout, or select a finished exercise to continue its remaining sets.'
              : 'Pick any exercise. Your completed sets stay saved.'}
          </p>
        </section>
      )}
      {item && <LoggedSets item={item} sets={session.sets} onEdit={setEditSet}/>}
      {session.status === 'partial' && <section className="notice"><p>Stopped for today. Progress is saved on this phone and will join your unfinished list when synced.</p><button onClick={() => void stopForToday()} disabled={busy}>Sync stopped workout</button></section>}
      {session.pointer.restEndAt && !readyToFinish && (
        <section
          className="rest-panel"
          aria-label="Rest timer after completed set"
        >
          <p
            className="eyebrow rest-announcement"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            Rest · Set complete
          </p>
          <div className="countdown" aria-live="polite">
            {String(Math.floor(secondsLeft / 60)).padStart(2, "0")}:
            {String(secondsLeft % 60).padStart(2, "0")}
          </div>
          <div className="rest-controls">
            <button onClick={() => changeRest(-15)}>−15 sec</button>
            <button className="button--danger-ghost" onClick={skipRest}>
              Skip
            </button>
            <button onClick={() => changeRest(15)}>+15 sec</button>
          </div>
          {item && (
            <p>
              Next: {item.exercise.name} · set {session.pointer.round}
            </p>
          )}
        </section>
      )}
      {item && !readyToFinish && (
        <section className="exercise-card" aria-labelledby="exercise-name">
          <div className="pair-label">
            Exercise {exerciseIndex + 1} of {exercises.length} · Set{" "}
            {session.pointer.round} of {item.sets}
          </div>
          <h1 id="exercise-name" tabIndex={-1}>
            {item.exercise.name}
          </h1>
          <p className="muted">{item.exercise.primaryMuscles.join(" · ")}</p>
          {item.notes && <p className="note-line">{item.notes}</p>}
          {item.progressionFlag && (
            <div className="progression-badge">
              {item.progressionFlag.suggestedIncrement
                ? `Increase ${item.progressionFlag.suggestedIncrement} kg when ready`
                : "Add load or use a controlled harder variation; record the variation in Exercise note"}
            </div>
          )}
          {!session.pointer.restEndAt && session.status !== "partial" && nextMissingExerciseSet(item,completedSlots) !== null && <form
            key={`${activeDraftKey}:${draftGeneration}`}
            ref={setForm}
            aria-label={`${item.exercise.name} set ${session.pointer.round}`}
            data-slot-key={activeDraftKey ?? ""}
            onSubmit={(event) => {
              event.preventDefault();
              void doneSet(event.currentTarget);
            }}
            noValidate
          >
            <div className="set-entry">
              <span className="set-number">Set {session.pointer.round}</span>
              {!["bodyweight", "count"].includes(item.exercise.loadMode) && (
                <label>
                  <span>{loadLabel(item)}</span>
                  <div className="stepper">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => stepLoad(-1)}
                      aria-label="Decrease weight"
                    >
                      −
                    </button>
                    <input
                      name="weight"
                      inputMode="decimal"
                      type="text"
                      autoComplete="off"
                      disabled={busy}
                      ref={(input) => {
                        if (input)
                          initializeDraftInput(input, "weight", weight);
                      }}
                      aria-label={`Weight in ${loadLabel(item)}`}
                    />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => stepLoad(1)}
                      aria-label="Increase weight by one increment"
                    >
                      +
                    </button>
                  </div>
                </label>
              )}
              {item.exercise.loadMode === "bodyweight" && (
                <>
                  <div className="bodyweight-pill">Bodyweight</div>
                  <label>
                    <span>Added load kg (optional)</span>
                    <input
                      name="weight"
                      inputMode="decimal"
                      type="text"
                      autoComplete="off"
                      disabled={busy}
                      ref={(input) => {
                        if (input)
                          initializeDraftInput(input, "weight", weight);
                      }}
                      placeholder="0"
                    />
                  </label>
                  <p className="muted small">
                    Add load here, or use Exercise note to track a harder
                    variation.
                  </p>
                </>
              )}
              {item.exercise.loadMode === "count" ? (
                <label>
                  <span>Completed count</span>
                  <input
                    name="count"
                    className="large-input"
                    inputMode="numeric"
                    type="text"
                    autoComplete="off"
                    disabled={busy}
                    ref={(input) => {
                      if (input) initializeDraftInput(input, "count", count);
                    }}
                  />
                </label>
              ) : (
                <label>
                  <span>Today’s reps</span>
                  <input
                    name="reps"
                    className="large-input"
                    inputMode="numeric"
                    type="text"
                    autoComplete="off"
                    disabled={busy}
                    ref={(input) => {
                      if (input) initializeDraftInput(input, "reps", reps);
                    }}
                    placeholder="Enter reps"
                    autoFocus
                  />
                </label>
              )}
            </div>
            <div className="previous">
              <span>Last time</span>
              <strong>
                {previous
                  ? formatLastPerformance(item.exercise.loadMode, previous)
                  : "—"}
              </strong>
            </div>
            <button type="submit" className="done-button" disabled={busy}>
              {busy ? "Saving…" : "Done set"} <span aria-hidden="true">✓</span>
            </button>
          </form>}
          <div className="exercise-actions">
            <button onClick={() => setDetails(item)}>Description</button>
            <a
              href={item.exercise.videoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Video ↗
            </a>
            <button onClick={() => void openHistory(item)}>History</button>
          </div>
          <div className="inline-actions workout-secondary-actions">
            {!currentExerciseHasSets && (
              <button onClick={() => void openSubstitute()}>Substitute</button>
            )}
            {item.finishedEarly ? <button onClick={()=>void finishExerciseEarly(false)} disabled={busy}>Continue this exercise</button>
              : nextMissingExerciseSet(item,completedSlots) !== null && <button onClick={()=>void finishExerciseEarly(true)} disabled={busy}>Finish exercise early</button>}
            <button onClick={()=>setEditExercise(item)}>Edit exercise</button>
            <button
              onClick={() => {
                setExerciseNoteFor(item);
                setExerciseNoteDraft(item.notes);
              }}
            >
              Exercise note
            </button>
            <button onClick={() => setNotesOpen(true)}>Session note</button>
          </div>
          <div className="next-up">
            <span>
              {session.pointer.round < item.sets
                ? "Next set"
                : "After this set"}
            </span>
            <strong>
              {session.pointer.round < item.sets
                ? `${item.exercise.name} · set ${session.pointer.round + 1}`
                : unfinishedOtherCount
                  ? "Choose any unfinished exercise"
                  : "Workout complete"}
            </strong>
          </div>
        </section>
      )}
      <button
        className="button button--danger-ghost button--full skip-workout"
        onClick={() => void stopForToday()}
        disabled={busy}
      >
        Stop for today
      </button>
      {editSet && <SetEditor sessionId={session.id} profileId={profileId} set={editSet} onClose={()=>setEditSet(null)} onSaved={applyRecord}/>}
      {editExercise && <ExerciseEditor sessionId={session.id} profileId={profileId} item={editExercise} onClose={()=>setEditExercise(null)} onSaved={applyRecord}/>}
      {historyRecordId && <SessionRecord id={historyRecordId} profileId={profileId} onClose={()=>setHistoryRecordId(null)} onResume={applyRecord} onChanged={()=>{}}/>}
      {details && !editSet && !editExercise && (
        <Modal title={details.exercise.name} onClose={() => setDetails(null)}>
          <p>
            <strong>{details.exercise.primaryMuscles.join(" · ")}</strong>
          </p>
          <p>{details.exercise.description}</p>
          <LoggedSets item={details} sets={session.sets} onEdit={setEditSet}/>
          <button onClick={()=>setEditExercise(details)}>Edit exercise</button>
          <h3>Three cues</h3>
          <ul>
            {details.exercise.cues.map((cue) => (
              <li key={cue}>{cue}</li>
            ))}
          </ul>
          <h3>Common mistakes</h3>
          <ul>
            {details.exercise.commonMistakes.map((mistake) => (
              <li key={mistake}>{mistake}</li>
            ))}
          </ul>
          <a
            className="button button--secondary button--full"
            href={details.exercise.videoUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Watch demonstration on YouTube
          </a>
        </Modal>
      )}
      {history && !historyRecordId && (
        <Modal
          title={`${history.item.exercise.name} history`}
          onClose={() => setHistory(null)}
        >
          {history.data === null ? (
            <Spinner label="Loading exercise history" />
          ) : history.data.trend.length === 0 ? (
            <p>No previous completed sets yet.</p>
          ) : (
            <>
              <p className="personal-best">
                <strong>
                  Personal best {history.data.personalBest.metric}
                </strong>
                <span>
                  {history.data.personalBest.value === null
                    ? "—"
                    : history.data.personalBest.metric === "count"
                      ? `${history.data.personalBest.value} count`
                      : history.data.personalBest.metric === "reps"
                        ? `Bodyweight · ${history.data.personalBest.value} reps`
                        : history.data.loadMode === "bodyweight"
                          ? `Bodyweight + ${history.data.personalBest.value} kg`
                          : `${history.data.personalBest.value} kg`}
                </span>
              </p>
              {trend.length > 1 && (
                <div
                  className="mini-trend"
                  role="img"
                  aria-label={`${history.data.personalBest.metric} trend: ${trend.map((row) => historyMetricValue(history.data!.personalBest.metric, row)).join(" to ")}`}
                >
                  {trend.map((row, index) => (
                    <span
                      key={`${row.sessionId}-${index}`}
                      className={`trend-level-${Math.min(10, Math.max(1, Math.ceil((historyMetricValue(history.data!.personalBest.metric, row) / Math.max(1, trendMax)) * 10)))}`}
                    />
                  ))}
                </div>
              )}
              <ol className="exercise-history-list">
                {history.data.trend.map((row, index) => (
                  <li key={`${row.sessionId}-${row.setNumber}-${index}`}>
                    <strong>
                      {formatDate(row.completedAt)} · set {row.setNumber}
                    </strong>
                    <span>
                      {formatExerciseResult(history.data!.loadMode, row)}
                    </span>
                    <button onClick={()=>setHistoryRecordId(row.sessionId)}>View / edit workout</button>
                  </li>
                ))}
              </ol>
            </>
          )}
        </Modal>
      )}
      {notesOpen && (
        <Modal title="Session notes" onClose={() => setNotesOpen(false)}>
          <Field label="Notes for this workout">
            <textarea
              value={notesDraft}
              onChange={(event) => setNotesDraft(event.target.value)}
              maxLength={2000}
            />
          </Field>
          <button
            className="button button--primary button--full"
            onClick={() => void saveNotes()}
          >
            Save notes
          </button>
        </Modal>
      )}
      {exerciseNoteFor && (
        <Modal
          title={`${exerciseNoteFor.exercise.name} note`}
          onClose={() => setExerciseNoteFor(null)}
        >
          <Field label="Exercise note for this workout">
            <textarea
              value={exerciseNoteDraft}
              onChange={(event) => setExerciseNoteDraft(event.target.value)}
              maxLength={500}
            />
          </Field>
          <button
            className="button button--primary button--full"
            onClick={() => void saveExerciseNote()}
          >
            Save exercise note
          </button>
        </Modal>
      )}
      {substituteFor && (
        <Modal
          title={`Substitute ${substituteFor.exercise.name}`}
          onClose={() => setSubstituteFor(null)}
        >
          <Field label="Replacement exercise">
            <select
              value={substituteId}
              onChange={(event) => setSubstituteId(event.target.value)}
            >
              <option value="">Choose an exercise…</option>
              {library
                .filter((entry) => entry.id !== substituteFor.exercise.id)
                .map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {entry.name}
                  </option>
                ))}
            </select>
          </Field>
          <button
            className="button button--primary button--full"
            onClick={() => void substitute()}
            disabled={!substituteId}
          >
            Use this exercise
          </button>
        </Modal>
      )}
    </div>
  );
}

// Queue-count updates belong to the surrounding shell. They must not reconcile
// an actively edited Safari number field simply because App rendered fresh
// callback functions. Real workout inputs still rerender for their own state,
// a new session/profile, or changed profile settings.
export const Workout = memo(
  WorkoutView,
  (previous, next) =>
    previous.profileId === next.profileId &&
    previous.settings === next.settings &&
    previous.initial === next.initial,
);
